import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle, Send } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowFloatingCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Earnings', href: '#chapter-01' },
    { label: 'Venues', href: '#chapter-02' },
    { label: 'Safety', href: '#chapter-03' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Apply', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass py-3' : 'py-6 bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.a
              href="/"
              className="flex items-center gap-3"
              data-testid="logo"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-10 h-10 rounded-full bg-[#E02852] flex items-center justify-center">
                <span className="font-serif text-xl font-semibold text-white">B</span>
              </div>
              <div className="hidden sm:block">
                <span className="font-serif text-xl text-[#F7F6F2] tracking-tight">
                  Brothel Jobs
                </span>
                <span className="block text-xs font-mono text-[#A1A1AA] tracking-widest uppercase">
                  Melbourne
                </span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="nav-link text-sm font-medium"
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.4 }}
                >
                  {item.label}
                </motion.a>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <motion.a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="hidden md:flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-sm font-medium"
                data-testid="nav-apply-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send size={16} />
                <span>Apply Confidentially</span>
              </motion.a>
              
              <motion.a
                href="sms:0429085683?body=Hi%20Lisa,%20I'm%20interested%20in%20confidential%20employment%20opportunities%20in%20Melbourne."
                className="hidden lg:flex items-center gap-2 btn-secondary px-4 py-2.5 rounded-full text-sm font-medium"
                data-testid="sms-cta-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MessageCircle size={16} />
                <span>SMS Lisa</span>
              </motion.a>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 text-[#F7F6F2]"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Floating Apply Button - appears after scrolling */}
      <AnimatePresence>
        {showFloatingCTA && (
          <motion.div
            className="fixed bottom-6 right-6 z-50"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            data-testid="floating-apply-btn"
          >
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="flex items-center gap-2 btn-primary px-6 py-4 rounded-full text-base font-medium shadow-lg shadow-[rgba(224,40,82,0.3)] hover:shadow-xl hover:shadow-[rgba(224,40,82,0.4)] transition-shadow"
            >
              <Send size={18} />
              <span>Apply Confidentially</span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.nav
              className="absolute top-20 left-6 right-6 bg-[#18181F] rounded-2xl p-6 border border-[rgba(255,255,255,0.08)]"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className="text-lg text-[#A1A1AA] hover:text-[#F7F6F2] transition-colors py-2"
                  >
                    {item.label}
                  </a>
                ))}
                <div className="pt-4 border-t border-[rgba(255,255,255,0.08)]">
                  <a
                    href="sms:0429085683?body=Hi%20Lisa,%20I'm%20interested%20in%20confidential%20employment%20opportunities%20in%20Melbourne."
                    className="flex items-center justify-center gap-2 btn-primary w-full py-3 rounded-full text-base font-medium"
                  >
                    <MessageCircle size={18} />
                    <span>SMS Lisa: 0429 085683</span>
                  </a>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
